import { isCompozzError } from "@dto/CompozzError";

// The record of the creation row exists on the server as soon as the row opens, and
// each of its cells is saved on its own. This tracks the writes of that record, so
// that closing the row knows whether something was entered, and can wait for the
// saves still in progress before refreshing the grid or deleting the record.
//
// It sits at the data layer (see @utils/data), which every write path goes through
// (typed values, links, paste, clear...). The POST creating the record does not go
// through it: the values prefilled by the view filters do not count as an input.

interface Session {
    recordKey: string;
    written: boolean;
    pending: Set<Promise<void>>;
}

export interface NewRowWrites {
    // Starts tracking a record, forgetting the previous one
    watch(recordKey: string): void;
    // Stops tracking the given record (or any record, without key)
    unwatch(recordKey?: string): void;
    // The tracked record
    recordKey(): string | null;
    // Tracks a write on the given records; the promise is returned as is
    write<T>(recordKeys: Array<string | null | undefined>, promise: Promise<T>): Promise<T>;
    // Tracks some work before a write (reading the clipboard...), which is no input by itself
    hold<T>(promise: Promise<T>): Promise<T>;
    // Whether something was saved, or may be being saved
    hasInput(): boolean;
    // Waits until nothing is in progress, and tells whether something was saved
    settled(): Promise<boolean>;
}

export function createNewRowWrites(): NewRowWrites {
    let session: Session | null = null;

    function track(target: Session, promise: Promise<unknown>, onResponse?: (response: unknown) => void) {
        const tracked: Promise<void> = promise
            .then(onResponse, () => { })
            .then(() => { target.pending.delete(tracked); });
        target.pending.add(tracked);
    }

    return {
        watch(recordKey) {
            session = { recordKey, written: false, pending: new Set() };
        },
        unwatch(recordKey) {
            if (recordKey === undefined || session?.recordKey === recordKey) {
                session = null;
            }
        },
        recordKey() {
            return session?.recordKey ?? null;
        },
        write(recordKeys, promise) {
            if (session && recordKeys.includes(session.recordKey)) {
                const target = session;
                track(target, promise, (response) => {
                    if (!isCompozzError(response)) {
                        target.written = true;
                    }
                });
            }
            return promise;
        },
        hold(promise) {
            if (session) {
                track(session, promise);
            }
            return promise;
        },
        hasInput() {
            return !!session && (session.written || session.pending.size > 0);
        },
        async settled() {
            const target = session;
            if (!target) {
                return false;
            }
            do {
                await Promise.allSettled(Array.from(target.pending));
                // A write may be chained to the previous one (delete the old link, add the
                // new one): it only starts a few microtasks after the previous one ended.
                await new Promise(resolve => setTimeout(resolve, 0));
            } while (target.pending.size > 0);
            return target.written;
        },
    };
}

export const newRowWrites = createNewRowWrites();
